import type { RequestConfig } from "@app/config/axios/http";
import { lotsServices } from "../lots";
import type { ApiStockLot } from "../lots";
import { stockOrdersServices, type StockOrderContentExtends, type StockOrderContentFilter } from "../stockOrders";
import { storePlacesApi } from "./api.core";
import type {
    ApiSpMovingData,
    ApiStorePlaceData,
    ApiStorePlaceRemainderData,
    SpMoving,
    SpMovingInsertData,
    StockPlacementContent,
    StockPlacementContentExtends,
    StorePlaceAvailableCellsParams,
    StorePlaceCancelPlacementParams,
    StorePlaceCancelSdbPlacementParams,
    StorePlacePlaceLotParams,
    StorePlacePlaceSdbParams,
    StorePlaceRemainder,
    StorePlaceRemainderExtends,
} from "./types";

/** Сервисный слой — места хранения, размещение партий и обогащение состава ПО. */
export const storePlacesServices = {
    ...storePlacesApi,

    /**
     * Закрепляет партию в ячейке через создание движения `spMoving.insert`.
     * @param params код партии, ячейки и количество
     * @param config опции HTTP-запроса
     */
    placeLot: async (params: StorePlacePlaceLotParams, config?: RequestConfig): Promise<void> => {
        await storePlacesApi.spMoving.insert({ data: buildSpMovingInsertPayload(params) }, config);
    },

    /**
     * Размещает количество в ячейке по конкретному движению `SdbCode`.
     * @param params код движения, ячейки и количество
     * @param config опции HTTP-запроса
     */
    placeBySdb: async (params: StorePlacePlaceSdbParams, config?: RequestConfig): Promise<void> => {
        await storePlacesApi.spMoving.insert({ data: buildSpMovingInsertPayload(params) }, config);
    },

    /**
     * Загружает последнее движение размещения для отмены в указанной ячейке.
     * @param params код партии и ячейки
     * @param config опции HTTP-запроса
     */
    loadLatestPlacementMovement: async (
        params: StorePlaceCancelPlacementParams,
        config?: RequestConfig,
    ): Promise<ApiSpMovingData | undefined> => {
        const movements = await loadPlacementMovements(params, config);
        return findLatestSpMoving(movements);
    },

    /**
     * Загружает последнее движение размещения по `SdbCode` (например `ArrivalSdbCode`) в ячейке.
     * @param params код складского движения и ячейки
     * @param config опции HTTP-запроса
     */
    loadLatestSdbPlacementMovement: async (
        params: Required<Pick<StorePlaceCancelSdbPlacementParams, "sdbCode" | "cellCode">>,
        config?: RequestConfig,
    ): Promise<ApiSpMovingData | undefined> => {
        const movements = await loadSdbPlacementMovements(params, config);
        return findLatestSpMoving(movements);
    },

    /**
     * Отменяет последнее размещение партии в ячейке через `spMoving.loadByFilter` и `spMoving.remove`.
     * @param params код партии и ячейки
     * @param config опции HTTP-запроса
     */
    cancelPlacement: async (params: StorePlaceCancelPlacementParams, config?: RequestConfig): Promise<void> => {
        const latestMovement = findLatestSpMoving(await loadPlacementMovements(params, config));

        if (latestMovement == null) {
            throw new Error("Движение размещения не найдено для отмены.");
        }

        await storePlacesApi.spMoving.remove({ code: latestMovement.Code }, config);
    },

    /**
     * Отменяет размещение по `SdbCode` через `spMoving.loadByFilter` + `spMoving.remove`. \
     * Без `cellCode` удаляет все неинвентаризационные движения с данным `SdbCode` \
     * (сценарий приемки СГП). С `cellCode` — только движения в указанной ячейке.
     * @param params код движения и опционально ячейки
     * @param config опции HTTP-запроса
     */
    cancelPlacementBySdb: async (params: StorePlaceCancelSdbPlacementParams, config?: RequestConfig): Promise<void> => {
        const movements = await loadSdbPlacementMovements(params, config);

        if (movements.length === 0) {
            throw new Error("Движение размещения не найдено для отмены.");
        }

        if (params.cellCode != null) {
            const latestMovement = findLatestSpMoving(movements);
            if (latestMovement == null) {
                throw new Error("Движение размещения не найдено для отмены.");
            }

            await storePlacesApi.spMoving.remove({ code: latestMovement.Code }, config);
            return;
        }

        await Promise.all(movements.map((movement) => storePlacesApi.spMoving.remove({ code: movement.Code }, config)));
    },

    /**
     * Загружает ячейки, доступные для размещения продукции на складе. \
     * Текущий контракт (до уточнения SAV): `DivisionCode` + `OnlyEndPoints: true`. \
     * Использовать и в picker выбора ячейки, и для валидации скана.
     * @param params код подразделения склада
     * @param config опции HTTP-запроса
     * @returns конечные ячейки подразделения
     */
    loadAvailableCellsForPlacement: async (
        params: StorePlaceAvailableCellsParams,
        config?: RequestConfig,
    ): Promise<ApiStorePlaceData[]> => {
        if (!isValidCode(params.divisionCode)) {
            throw new Error("Для загрузки доступных ячеек размещения нужен код подразделения склада.");
        }

        return storePlacesApi.load(
            {
                filter: {
                    DivisionCode: params.divisionCode,
                    OnlyEndPoints: true,
                },
            },
            config,
        );
    },

    /**
     * Проверяет, входит ли ячейка в список доступных для размещения на складе.
     * @param params код подразделения и код ячейки
     * @param config опции HTTP-запроса
     * @returns `true`, если ячейка найдена среди доступных
     */
    isCellAvailableForPlacement: async (
        params: StorePlaceAvailableCellsParams & { cellCode: number },
        config?: RequestConfig,
    ): Promise<boolean> => {
        if (!isValidCode(params.divisionCode)) {
            throw new Error("Для проверки доступности ячейки нужен код подразделения склада.");
        }

        if (!isValidCode(params.cellCode)) {
            throw new Error("Для проверки доступности ячейки нужен код ячейки.");
        }

        const cells = await storePlacesApi.load(
            {
                filter: {
                    Codes: [params.cellCode],
                    DivisionCode: params.divisionCode,
                    OnlyEndPoints: true,
                },
            },
            config,
        );

        return cells.some((cell) => cell.Code === params.cellCode);
    },

    /**
     * Загружает остатки по местам хранения и догружает связанные сущности.
     * @param params фильтр остатков и include
     * @param config опции HTTP-запроса
     */
    remainders: {
        load: async (
            params: {
                filter?: Parameters<typeof storePlacesApi.loadRemainders>[0]["filter"];
                include?: Array<keyof StorePlaceRemainderExtends>;
            },
            config?: RequestConfig,
        ): Promise<StorePlaceRemainder[]> => {
            const { filter, include } = params;
            const remainders = await storePlacesApi.loadRemainders({ filter }, config);

            if (include == null) {
                return remainders;
            }

            const includeStorePlace = include.includes("StorePlace");
            const includeLot = include.includes("Lot");
            const storePlaceCodes = new Set<number>();
            const lotCodes = new Set<number>();

            remainders.forEach((remainder) => {
                if (includeStorePlace && isValidCode(remainder.SpCode)) {
                    storePlaceCodes.add(remainder.SpCode);
                }
                if (includeLot && isValidCode(remainder.LotCode)) {
                    lotCodes.add(remainder.LotCode);
                }
            });

            const storePlaces = new Map<number, ApiStorePlaceData>();
            const lots = new Map<number, ApiStockLot>();
            const promises: Promise<unknown>[] = [];

            if (storePlaceCodes.size > 0) {
                promises.push(
                    storePlacesApi.load({ filter: { Codes: [...storePlaceCodes] } }, config).then((items) => {
                        items.forEach((storePlace) => storePlaces.set(storePlace.Code, storePlace));
                    }),
                );
            }

            if (lotCodes.size > 0) {
                promises.push(
                    lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                        items.forEach((lot) => lots.set(lot.LotCode, lot));
                    }),
                );
            }

            await Promise.all(promises);

            return remainders.map((remainder) =>
                serializeRemainder(remainder, {
                    StorePlace:
                        includeStorePlace && isValidCode(remainder.SpCode) ? storePlaces.get(remainder.SpCode) : undefined,
                    Lot: includeLot && isValidCode(remainder.LotCode) ? lots.get(remainder.LotCode) : undefined,
                }),
            );
        },
    },

    content: {
        /**
         * Загружает состав приходного ордера для размещения ТМЦ с рекомендациями и движениями в ячейках.
         * @param params фильтр, include и лимит рекомендаций
         * @param config опции HTTP-запроса
         */
        loadForPlacement: async (
            params: {
                flt?: StockOrderContentFilter;
                include?: Array<keyof StockPlacementContentExtends>;
                recommendedCellsLimit?: number;
            },
            config?: RequestConfig,
        ): Promise<StockPlacementContent[]> => {
            const { flt, include, recommendedCellsLimit = 3 } = params;

            if (include == null) {
                const contents = await stockOrdersServices.content.load({ flt }, config);
                return contents;
            }

            const includeStockObj = include.includes("StockObj");
            const includeMeasure = include.includes("Measure");
            const includeLot = include.includes("Lot");
            const includeDepotCard = include.includes("DepotCard");
            const includeRecommendedCells = include.includes("RecommendedCells");
            const includeSpMovings = include.includes("SpMovings");

            const orderIncludes: Array<keyof StockOrderContentExtends> = [];
            if (includeDepotCard) {
                orderIncludes.push("DepotCard");
            }
            if (includeStockObj) {
                orderIncludes.push("StockObj");
            }
            if (includeMeasure) {
                orderIncludes.push("Measure");
            }
            if (includeLot || includeRecommendedCells || includeSpMovings) {
                orderIncludes.push("Lot");
            }

            const contents = await stockOrdersServices.content.load(
                {
                    flt,
                    include: orderIncludes.length > 0 ? orderIncludes : undefined,
                },
                config,
            );

            if (!includeRecommendedCells && !includeSpMovings) {
                return contents;
            }

            const lotCodes = new Set<number>();
            contents.forEach((content) => {
                if (isValidCode(content.LotCode)) {
                    lotCodes.add(content.LotCode);
                }
            });

            const recommendedCells = new Map<number, StockPlacementContent["RecommendedCells"]>();
            const spMovings = new Map<number, SpMoving[]>();
            const promises: Promise<unknown>[] = [];

            if (includeRecommendedCells && lotCodes.size > 0) {
                promises.push(
                    storePlacesApi
                        .getRecommendedCellsForLot(
                            {
                                lotCodes: [...lotCodes],
                                nCellsToFind: recommendedCellsLimit,
                            },
                            config,
                        )
                        .then((items) => {
                            items.forEach((item) => {
                                if (!isValidCode(item.LotCode) || item.StorePlace == null) {
                                    return;
                                }

                                const cells = recommendedCells.get(item.LotCode) ?? [];
                                cells.push(item.StorePlace);
                                recommendedCells.set(item.LotCode, cells);
                            });
                        }),
                );
            }

            if (includeSpMovings && lotCodes.size > 0) {
                promises.push(
                    storePlacesApi.spMoving
                        .loadByFilter(
                            {
                                filter: {
                                    SdbCodes: [...lotCodes],
                                    OnlyNotInv: true,
                                },
                            },
                            config,
                        )
                        .then((movements) => {
                            movements.forEach((movement) => {
                                if (!isValidCode(movement.SdbCode)) {
                                    return;
                                }

                                const items = spMovings.get(movement.SdbCode) ?? [];
                                items.push(movement);
                                spMovings.set(movement.SdbCode, items);
                            });
                        }),
                );
            }

            await Promise.all(promises);

            if (includeSpMovings) {
                await enrichSpMovingsWithStorePlaces(spMovings, config);
            }

            return contents.map((content) => ({
                ...content,
                RecommendedCells:
                    includeRecommendedCells && isValidCode(content.LotCode)
                        ? recommendedCells.get(content.LotCode)
                        : undefined,
                SpMovings: includeSpMovings && isValidCode(content.LotCode) ? spMovings.get(content.LotCode) : undefined,
            }));
        },

        /**
         * Загружает одну строку состава ПО для размещения с теми же placement-include.
         * @param params код строки, код ПО, include и лимит рекомендаций
         * @param config опции HTTP-запроса
         */
        loadForPlacementByCode: async (
            params: {
                code: number;
                orderCode: number;
                include?: Array<keyof StockPlacementContentExtends>;
                recommendedCellsLimit?: number;
            },
            config?: RequestConfig,
        ): Promise<StockPlacementContent | null> => {
            const contents = await storePlacesServices.content.loadForPlacement(
                {
                    flt: {
                        Codes: [params.code],
                        StockOrderCode: params.orderCode,
                    },
                    include: params.include,
                    recommendedCellsLimit: params.recommendedCellsLimit,
                },
                config,
            );

            return contents[0] ?? null;
        },
    },
} as const;

async function loadPlacementMovements(
    params: StorePlaceCancelPlacementParams,
    config?: RequestConfig,
): Promise<ApiSpMovingData[]> {
    return loadSdbPlacementMovements({ sdbCode: params.lotCode, cellCode: params.cellCode }, config);
}

async function loadSdbPlacementMovements(
    params: StorePlaceCancelSdbPlacementParams,
    config?: RequestConfig,
): Promise<ApiSpMovingData[]> {
    return storePlacesApi.spMoving.loadByFilter(
        {
            filter: {
                SdbCodes: [params.sdbCode],
                SpCodes: params.cellCode != null ? [params.cellCode] : undefined,
                OnlyNotInv: true,
            },
        },
        config,
    );
}

function buildSpMovingInsertPayload(params: StorePlacePlaceLotParams | StorePlacePlaceSdbParams): SpMovingInsertData {
    return {
        Code: 0,
        SdbCode: resolveSdbCode(params),
        SpCode: params.cellCode,
        Qty: params.qty,
        IsArchive: 0,
        IsInv: false,
    };
}

function resolveSdbCode(params: StorePlacePlaceLotParams | StorePlacePlaceSdbParams): number {
    if ("sdbCode" in params) {
        return params.sdbCode;
    }

    return params.lotCode;
}

function findLatestSpMoving(movements: ApiSpMovingData[]): ApiSpMovingData | undefined {
    if (movements.length === 0) {
        return undefined;
    }

    return [...movements].sort(compareSpMovingByRecency)[0];
}

function compareSpMovingByRecency(left: ApiSpMovingData, right: ApiSpMovingData): number {
    const leftTimestamp = resolveSpMovingTimestamp(left);
    const rightTimestamp = resolveSpMovingTimestamp(right);

    if (leftTimestamp !== rightTimestamp) {
        return rightTimestamp - leftTimestamp;
    }

    return right.Code - left.Code;
}

function resolveSpMovingTimestamp(movement: ApiSpMovingData): number {
    const userDate = Date.parse(movement.UserDate);
    if (!Number.isNaN(userDate)) {
        return userDate;
    }

    const confirmDate = Date.parse(movement.ConfirmDate);
    if (!Number.isNaN(confirmDate)) {
        return confirmDate;
    }

    return movement.Code;
}

function serializeRemainder(
    remainder: ApiStorePlaceRemainderData,
    includeData?: Partial<StorePlaceRemainderExtends>,
): StorePlaceRemainder {
    return {
        ...remainder,
        StorePlace: includeData?.StorePlace,
        Lot: includeData?.Lot,
    };
}

/**
 * Догружает обозначения ячеек для движений размещения через `stock/storePlaces.loadByFilter`.
 * @param spMovings движения, сгруппированные по `SdbCode`/`LotCode`
 * @param config опции HTTP-запроса
 */
async function enrichSpMovingsWithStorePlaces(spMovings: Map<number, SpMoving[]>, config?: RequestConfig): Promise<void> {
    const storePlaceCodes = new Set<number>();

    spMovings.forEach((movements) => {
        movements.forEach((movement) => {
            if (isValidCode(movement.SpCode)) {
                storePlaceCodes.add(movement.SpCode);
            }
        });
    });

    if (storePlaceCodes.size === 0) {
        return;
    }

    const storePlaces = new Map<number, ApiStorePlaceData>();
    const items = await storePlacesApi.load({ filter: { Codes: [...storePlaceCodes] } }, config);
    items.forEach((storePlace) => storePlaces.set(storePlace.Code, storePlace));

    spMovings.forEach((movements, sdbCode) => {
        spMovings.set(
            sdbCode,
            movements.map((movement) => ({
                ...movement,
                StorePlace: isValidCode(movement.SpCode) ? storePlaces.get(movement.SpCode) : undefined,
            })),
        );
    });
}

function isValidCode(code: number | null | undefined): code is number {
    return code != null && code !== 0;
}
